import React, { useState } from "react";
import {
  Upload,
  Button,
  Spin,
  Typography,
  Card,
  message,
  Row,
  Col,
  Modal,
} from "antd";
import {
  UploadOutlined,
  FileSearchOutlined,
  CheckCircleTwoTone,
} from "@ant-design/icons";
import analyzerService from "../../services/analyzer";
import { useAuth } from "../../contexts/AuthContext";

const { Title, Text } = Typography;

const EyeHealthAnalyzer = () => {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [reportMessage, setReportMessage] = useState(null);
  const [file, setFile] = useState(null);
  const [supplementMsg, setSupplementMsg] = useState(null);
  const [supplementLoader, setSupplementLoader] = useState(false);
  const [aiText, setAiText] = useState("");
  const [imgPath, setImgPath] = useState("");

  const { user } = useAuth();

  const showSuccessModal = (title, content) => {
    Modal.success({
      title,
      content,
      centered: true,
      okButtonProps: {
        style: { backgroundColor: "#1c5a85", borderColor: "#1c5a85" },
      },
    });
  };

  const handleAnalyze = async () => {
    if (!file) {
      message.warning("Please upload an image first!");
      return;
    }
    setLoading(true);
    setReportMessage("");
    try {
      const response = await analyzerService.uploadImage(file, user.email);
      if (response?.success) {
        message.success("Analysis complete!");
        setImgPath(response.urlPath);
        setAiText(response.aiText);
        setReportMessage(response.message);

        showSuccessModal(
          "Analysis Complete ✅",
          "Your report has been successfully generated and emailed to you."
        );
      } else {
        message.error("Analysis failed. Try again.");
        setImageUrl(null);
      }
    } catch (err) {
      console.error(err);
      message.error("Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async ({ file }) => {
    setImageUrl(URL.createObjectURL(file));
    setFile(file);
  };

  const handleGetSupplements = async () => {
    setSupplementLoader(true);
    try {
      const response = await analyzerService.getSupplements(
        imgPath,
        aiText,
        user.email
      );

      if (response.success) {
        setSupplementMsg(response.message);
        showSuccessModal(
          "Supplement Plan Ready 🌿",
          "Your supplement recommendations have been generated and emailed successfully."
        );
      } else {
        message.error("Failed to generate supplements.");
      }
    } catch (error) {
      console.error(error);
      message.error("Something went wrong while fetching supplements.");
    } finally {
      setSupplementLoader(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-[calc(100vh-80px)] px-6 py-10">
      {/* Header */}
      <div className="mb-12 text-center">
        <Title
          level={3}
          className="!m-0 !text-[#1c5a85] font-bold tracking-wide"
        >
          Eye on Health Analyzer
        </Title>
        <Text className="text-gray-500 text-sm">
          Upload a retina image to get instant health insights —{" "}
          private, secure, and fast.
        </Text>
      </div>

      <Row gutter={[32, 32]} justify="center" align="stretch">
        {/* Upload Section */}
        <Col xs={24} md={10}>
          <Card
            bordered={false}
            className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg transition-all p-8 bg-white flex flex-col justify-between h-full"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center flex-1">
                <Spin size="large" className="text-[#1c5a85]" />
                <Text className="text-gray-500 mt-3 text-sm">
                  Analyzing image...
                </Text>
              </div>
            ) : !imageUrl ? (
              <div className="flex flex-col items-center justify-center flex-1 space-y-6">
                <Upload
                  accept="image/*"
                  customRequest={handleUpload}
                  showUploadList={false}
                >
                  <Button
                    icon={<UploadOutlined />}
                    className="!bg-[#1c5a85] !text-white !border-none !rounded-md px-6 py-5 text-base hover:!bg-[#174b6e]"
                  >
                    Upload Eye Image
                  </Button>
                </Upload>
                <Text className="text-gray-400 text-xs">
                  Supported formats: JPG, PNG — up to 10MB
                </Text>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center flex-1">
                <img
                  src={imageUrl}
                  alt="Uploaded eye"
                  className="w-56 h-56 object-cover rounded-xl border border-gray-200 mb-6 shadow-sm"
                />
                <div className="flex gap-3">
                  <Button
                    type="primary"
                    className="!bg-[#1c5a85] !border-none !rounded-md px-8 hover:!bg-[#174b6e]"
                    onClick={handleAnalyze}
                    disabled={supplementLoader}
                  >
                    Analyze
                  </Button>
                  <Button
                    danger
                    className="!rounded-md px-8"
                    onClick={() => {
                      setFile(null);
                      setImageUrl(null);
                      setReportMessage(null);
                      setSupplementMsg(null);
                    }}
                    disabled={supplementLoader}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </Col>

        {/* Results Section */}
        <Col xs={24} md={10}>
          <Card
            bordered={false}
            className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg transition-all p-8 bg-white flex flex-col justify-between h-full"
          >
            {supplementLoader ? (
              <div className="flex flex-col items-center justify-center flex-1">
                <Spin size="large" className="text-[#1c5a85]" />
                <Text className="text-gray-500 mt-3 text-sm">
                  Generating supplements...
                </Text>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <FileSearchOutlined className="text-[#1c5a85] text-2xl" />
                  <Title
                    level={4}
                    className="!m-0 !text-[#1c5a85] font-semibold"
                  >
                    Health Report
                  </Title>
                </div>

                <div className="text-gray-600 text-sm leading-relaxed flex-1 min-h-[180px]">
                  {loading ? (
                    <p>Processing your eye image...</p>
                  ) : imageUrl ? (
                    reportMessage ? (
                      <div className="flex flex-col items-start space-y-3">
                        <div className="flex items-center gap-2 text-green-600 font-medium">
                          <CheckCircleTwoTone twoToneColor="#52c41a" />
                          <span>Analysis completed</span>
                        </div>
                        <p className="mt-1">{reportMessage}</p>
                        {supplementMsg && (
                          <p className="text-[#1c5a85]">{supplementMsg}</p>
                        )}
                      </div>
                    ) : (
                      <>
                        <p>
                          <span className="font-medium text-[#1c5a85]">
                            Ready for analysis.
                          </span>{" "}
                          Click <b>Analyze</b> to start the diagnostic.
                        </p>
                        <ul className="list-disc pl-5 mt-3 text-gray-500">
                          <li>Diabetic Retinopathy</li>
                          <li>Macular Degeneration</li>
                          <li>Glaucoma</li>
                        </ul>
                      </>
                    )
                  ) : (
                    <p>
                      Upload an eye image to begin automated diagnostic analysis.
                      Your image is processed securely and not stored.
                    </p>
                  )}
                </div>
              </>
            )}
          </Card>

          {imageUrl && !loading && reportMessage && (
            <Button
              className="!mt-4 !bg-[#1c5a85]/10 !border !border-[#1c5a85] !text-[#1c5a85] !rounded-md px-6 hover:!bg-[#174b6e] hover:!text-white w-full transition-all"
              onClick={handleGetSupplements}
              disabled={supplementLoader}
            >
              Generate Supplement Plan
            </Button>
          )}
        </Col>
      </Row>

      {/* Footer */}
      <div className="text-gray-400 text-xs mt-12 text-center">
        © 2025 <span className="text-[#1c5a85] font-medium">Eye on Health</span>{" "}Wellness
      </div>
    </div>
  );
};

export default EyeHealthAnalyzer;
